import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BookOpen, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";

export const Route = createFileRoute("/app/renata/playbooks")({
  component: PlaybooksPage,
  head: () => ({ meta: [{ title: "Guías de trabajo — Renata" }] }),
});

function PlaybooksPage() {
  const tenantId = useWorkspaceTenantId();
  const [playbooks, setPlaybooks] = useState<Array<Record<string, unknown>>>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    void fetch("/api/renata/planning?view=playbooks", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    })
      .then((r) => r.json())
      .then((d) => setPlaybooks(d.playbooks ?? []));
  }, [tenantId]);

  async function start(id: string) {
    if (!tenantId) return;
    setBusy(id);
    try {
      const res = await fetch("/api/renata/planning", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "playbook", playbookId: id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(String((d as { error?: string }).error ?? "No se pudo iniciar la guía"));
        return;
      }
      toast.success("Borrador creado. Abrí el Planificador para revisarlo y aprobarlo.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
      <RenataPageChrome
        screenId="playbooks"
        title="Guías de trabajo"
        subtitle="Recetas listas del negocio. Al iniciar se crea un borrador para aprobar — no se ejecuta solo."
      />
      <div className="rounded-lg border border-border bg-card p-3 text-xs text-foreground/85 space-y-1">
        <p><strong>Qué hace “Iniciar borrador”:</strong> prepara un plan a partir de la guía. Después aparece en el Planificador para aprobar.</p>
        <p><strong>Tu siguiente paso:</strong> iniciá una guía → andá al Planificador → aprobá → seguí en Planes de trabajo.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {playbooks.map((p) => (
          <div key={String(p.id)} className="rounded-xl border p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h2 className="font-medium text-sm">{String(p.title)}</h2>
              <Badge variant="secondary">{String(p.risk)}</Badge>
            </div>
            <p className="text-xs text-foreground/80 line-clamp-3">{String(p.description)}</p>
            <p className="text-[11px] text-muted-foreground">
              {String(p.estimatedDays)}d · {String(p.estimatedImpact)}
            </p>
            <Button size="sm" disabled={busy === p.id} onClick={() => void start(String(p.id))}>
              <Play className="h-3.5 w-3.5 mr-1" />
              {busy === p.id ? "…" : "Iniciar borrador"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
